import {
  LongTextCellRenderer,
  CompanyNameRenderer,
  QualifiedCellRenderer,
  StatusPriorityScoreRenderer,
  dateFormatter
} from '../SalesCellRenderers';
import { buildLookupMaps } from '../../../utils/SalesTableUtils';

/**
 * Build column definitions for sales leads or opportunities table
 * @param {Object} config - Configuration object
 * @param {Function} config.ActionsRenderer - Component to render action buttons
 * @param {Array} config.contactStatuses - Array of contact status objects
 * @param {Array} config.priorities - Array of priority objects
 * @param {string} config.entityType - 'lead' or 'opportunity'
 * @returns {Array} AG Grid column definitions
 */
export const buildSalesColumns = (config) => {
  const {
    ActionsRenderer,
    contactStatuses = [],
    priorities = [],
    entityType = 'lead'
  } = config;

  const {
    statusNameToId,
    statusIdToName,
    statusNames
  } = buildLookupMaps(contactStatuses, priorities);

  const columns = [
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsRenderer,
      pinned: '',
      width: 150,
      sortable: false,
      filter: false,
      suppressMovable: true
    },
    {
      headerName: 'Company',
      field: 'company',
      cellRenderer: CompanyNameRenderer,
      width: 250,
      editable: false,
      pinned: ''
    },
    {
      headerName: 'Contact Persons',
      field: 'contact_persons',
      valueGetter: (params) => {
        const contactPersons = params.data?.contact_persons;
        const prospectName = params.data?.prospect_name;

        // Use lightweight contact_persons array from serializer (just names)
        if (contactPersons && Array.isArray(contactPersons) && contactPersons.length > 0) {
          if (contactPersons.length === 1) {
            return contactPersons[0];
          } else if (contactPersons.length > 1) {
            return `${contactPersons[0]} +${contactPersons.length - 1} more`;
          }
        }

        // Fall back to prospect_name from JSONField (for backward compatibility)
        if (prospectName) return prospectName;

        return 'No Contact';
      },
      tooltipValueGetter: (params) => {
        const contactPersons = params.data?.contact_persons;
        if (contactPersons && Array.isArray(contactPersons) && contactPersons.length > 0) {
          return contactPersons.join('\n');
        }
        return params.data?.prospect_name || 'No Contact';
      },
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Source',
      field: 'source',
      valueGetter: (params) => params.data?.source_name || '',
      width: 130,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Lead Type',
      field: 'lead_type',
      valueGetter: (params) => params.data?.lead_type_name || '',
      width: 130,
      filter: 'agSetColumnFilter'
    },
    // Lead Tracking Columns - Combined Status, Priority, Score
    {
      headerName: 'Status | Priority | Score',
      field: 'contact_status',
      cellRenderer: StatusPriorityScoreRenderer,
      valueGetter: (params) => params.data?.contact_status_name || statusIdToName[params.data?.contact_status] || '',
      valueSetter: (params) => {
        const statusId = statusNameToId[params.newValue];
        if (statusId) {
          params.data.contact_status = statusId;
          params.data.contact_status_name = params.newValue;
        }
        return true;
      },
      width: 180,
      filter: 'agSetColumnFilter',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: statusNames
      }
    },
    {
      headerName: 'Assigned To',
      field: 'assigned_to',
      valueGetter: (params) => params.data?.assigned_to_name || 'Unassigned',
      width: 140,
      editable: false
    },
    {
      headerName: 'Organization',
      field: 'organization',
      valueGetter: (params) => params.data?.organization_name || 'Unassigned',
      width: 150,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Notes',
      field: 'notes',
      width: 250,
      cellRenderer: LongTextCellRenderer,
      editable: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: {
        maxLength: 5000,
        rows: 5,
        cols: 50
      },
      tooltipField: 'notes'
    },
    {
      headerName: 'External ID',
      field: 'external_id',
      width: 120,
      filter: 'agSetColumnFilter'
    },
    // Metadata Columns
    {
      headerName: `${entityType === 'lead' ? 'Lead' : 'Opportunity'} ID`,
      field: 'id',
      width: 110,
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'Lead Type',
      field: 'lead_type',
      valueGetter: (params) => params.data?.lead_type_name || '',
      width: 130,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Created At',
      field: 'created_at',
      width: 140,
      valueFormatter: dateFormatter,
      filter: 'agDateColumnFilter'
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      valueGetter: (params) => params.data?.created_by_name || '',
      width: 130,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Updated At',
      field: 'updated_at',
      width: 140,
      valueFormatter: dateFormatter,
      filter: 'agDateColumnFilter'
    }
  ];

  // Add lead-specific columns
  if (entityType === 'lead') {
    // Insert after "Score" column
    const scoreIndex = columns.findIndex(col => col.field === 'contact_status');
    columns.splice(scoreIndex + 1, 0,
      {
        headerName: 'Qualified',
        field: 'is_qualified',
        cellRenderer: QualifiedCellRenderer,
        width: 150,
        filter: 'agSetColumnFilter'
      }
    );
  }

  return columns;
};

/**
 * Default column definitions for AG Grid
 */
export const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 90,
  editable: false,
  suppressMovable: false
};
